/**
 * De selectie die de objectenlijst en de portefeuillekaart delen.
 *
 * Jans wens van 07-09-2026: filteren bovenaan de kolommen van de lijst, en
 * dezelfde selectie op de kaart zien. Zijn aanvulling van 08-09-2026: meer dan
 * één waarde per kolom ("label e, f, g"), een filter boven élke kolom, en
 * kunnen filteren op de kaart zelf in plaats van alleen in de lijst.
 *
 * Dat werkt alleen als beide schermen hetzelfde begrip van "de selectie"
 * hebben, en als die selectie van het ene scherm naar het andere kan reizen.
 * Daarvoor is de URL het aangewezen middel:
 * `/objecten?city=Twello&city=Gorssel&energy_label=E&energy_label=F` en
 * `/kaart?…` met dezelfde querystring zijn dezelfde selectie, en een link
 * ernaar is deelbaar.
 *
 * De sleutels zijn letterlijk de querystring-parameters van de API
 * (`backend/app/services/asset_filters.py`). Het scherm vertaalt dus niets;
 * het geeft door. Wat een filter betekent — welke labels als "A" tellen, wat
 * "onbekend" is, hoe "E, F of G" er in woorden uitziet — beslist de API, en die
 * beschrijft de actieve filters ook in gewone taal (`filters.active`), zodat
 * lijst en kaart dezelfde woorden gebruiken.
 */

/** Een kolom waarin meer dan één waarde tegelijk gekozen mag worden. */
export const CHOICE_KEYS = [
  // De geografie staat van groot naar klein: gemeente, plaats, wijk, buurt.
  // Die volgorde is niet cosmetisch — de API laat de keuzelijst van elke kolom
  // inkrimpen op de kolommen bóven zich (zie asset_filters.HIERARCHY).
  "unit_type", "segment", "status",
  "municipality", "city", "district", "neighbourhood",
  // `label_comparison` is de eigen opgave naast het EP-Online-register:
  // gelijk, afwijkend, alleen eigen opgave, alleen EP-Online, geen van beide,
  // of "onbekend" voor bezit dat nog niet is opgehaald.
  "energy_label", "label_comparison", "pmc",
  // Het woningtype uit de eigen administratie van de corporatie (ViewPoint,
  // "VHO type"): tussenwoning, hoekwoning, … (Jan, 23-09-2026, FB-044).
  "dwelling_type",
  // Het woningtype afgeleid uit de BAG volgens de Kadaster-methode, als letter
  // V/K/H/T/A/N (Jan, 30-09-2026).
  "bag_dwelling_type",
  // De NEN 2767-conditiescore uit O-Prognose, "1" t/m "6" (Jan, 24-09-2026).
  "condition",
  // De voorkeurstrategie van het complex (RIGO-advies, geen besluit) als
  // hoofdrichting: doorexploiteren, herpositionering, herstructurering,
  // verkoop of undetermined (FB-071/FB-073). Leest de vastgelegde uitkomst.
  "strategy",
  // De vastgestelde strategiestatus (R4): een van Jans zeven waarden op het
  // pand, anders op het complex, of "onbekend" = niet vastgesteld. En het
  // consistentiesignaal tegen het sloopbesluit (§2.2.2), in beide richtingen.
  "strategy_status", "strategy_signal",
  // Complexcodes (FB-089): meer complexen tegelijk is een bundeling. Begon als
  // doorklik vanaf het complexscherm (één code); "onbekend" = zonder complex.
  "property_complex",
  // Plan 4b (FB-070): de functie van het pand en van het complex van het
  // object (sociale huur, BOG, … of `combination`), het daktype van het pand
  // uit 3DBAG en het BAG-gebruiksdoel van het verblijfsobject. Een pand- of
  // complexfilter selecteert de objecten daarin.
  "building_function", "complex_function", "roof_type", "use_purpose",
  // Het vastgelegde hoofdscenario (continuation, transformation, addition of
  // "onbekend" = niet vastgelegd) op het object, zijn pand of zijn complex
  // (FB-078 deel 2).
  "scenario",
  // De waarderingsroute van het handboek (R1, maandagbrief 12-10-2026): regular,
  // demolition, flex, third_party_lease, bog, mog, zog, parking, not_computable
  // of "onbekend" = nog geen run met route. Uit de beleidswaarderun.
  "valuation_route",
  // Datakwaliteit tegen de BAG (FB-056): `go_bag` en `type_bag`. Komt binnen
  // via een link uit de aandachtspuntenbalk of het objectscherm, niet via een
  // kolomfilter; de "gefilterd op"-balk toont hem met de band erbij.
  "data_signal",
] as const;

export type ChoiceKey = (typeof CHOICE_KEYS)[number];

/**
 * Een kolom met een van/tot-filter. Dezelfde sleutels als `RANGES` in de API;
 * de querystring heet `<sleutel>_from` en `<sleutel>_to`.
 */
export const RANGE_KEYS = [
  "build_year", "renovation_year", "floor_area", "bag_floor_area", "bvo_estimate", "energy_performance_ep2", "contract_rent",
  "woz_value", "vacant_value", "market_value", "policy_value",
  // Plan 4b: het geschatte einde van de levensduur (zoals de kolom) en de
  // kenmerken van het pand uit 3DBAG.
  "lifespan_end",
  // FB-093: huur min onderhoud, beheer en zakelijke lasten per jaar.
  "operating_result",
  "building_height", "building_floors", "building_exterior_wall", "building_roof",
  "building_party_wall", "building_ground",
] as const;

export type RangeKey = (typeof RANGE_KEYS)[number];

/**
 * Tekstfilters die één op één in de querystring staan. `building` is er een
 * van een andere soort: geen zoekterm maar een sleutel waarop je doorklikt
 * vanaf het pandscherm, en hij matcht exact. Het complex is sinds FB-089 een
 * keuzekolom (`property_complex` in `CHOICE_KEYS`).
 */
const TEXT_KEYS = ["search", "address", "building"] as const;

export type Bounds = { from?: number; to?: number };

export type AssetFilters = {
  search?: string;
  address?: string;
  /** Precies dit pand (de BAG-pandsleutel), gezet door doorklikken. */
  building?: string;
  /** Per keuzekolom nul of meer waarden; binnen een kolom geldt OF. */
  choices?: Partial<Record<ChoiceKey, string[]>>;
  ranges?: Partial<Record<RangeKey, Bounds>>;
  in_cashflow_only?: boolean;
  /**
   * Bewaarde deelportefeuilles, als id (FB-076, besluit B6). Meer dan één is
   * de vereniging van hun definities; de andere filters gelden er bovenop.
   */
  sub_portfolio?: string[];
};

/** De querystring-sleutel van de gekozen deelportefeuilles. */
export const SUB_PORTFOLIO_KEY = "sub_portfolio";

const nummer = (raw: string | null): number | undefined => {
  if (raw == null || raw.trim() === "") return undefined;
  const value = Number(raw.replace(",", "."));
  return Number.isFinite(value) ? value : undefined;
};

/** Van URL naar filter. Onbruikbare waarden vallen weg; de API valideert de rest. */
export function filtersFromSearchParams(params: URLSearchParams): AssetFilters {
  const filters: AssetFilters = {};
  for (const key of TEXT_KEYS) {
    const value = params.get(key)?.trim();
    if (value) filters[key] = value;
  }
  const choices: Partial<Record<ChoiceKey, string[]>> = {};
  for (const key of CHOICE_KEYS) {
    const values = params.getAll(key).map((value) => value.trim()).filter(Boolean);
    if (values.length > 0) choices[key] = [...new Set(values)];
  }
  if (Object.keys(choices).length > 0) filters.choices = choices;

  const ranges: Partial<Record<RangeKey, Bounds>> = {};
  for (const key of RANGE_KEYS) {
    const from = nummer(params.get(`${key}_from`));
    const to = nummer(params.get(`${key}_to`));
    if (from !== undefined || to !== undefined) ranges[key] = { from, to };
  }
  if (Object.keys(ranges).length > 0) filters.ranges = ranges;

  if (params.get("in_cashflow_only") === "true") filters.in_cashflow_only = true;
  // Herhaald (`sub_portfolio=a&sub_portfolio=b`), maar `a,b` in één waarde ook.
  const delen = params.getAll(SUB_PORTFOLIO_KEY)
    .flatMap((value) => value.split(",")).map((value) => value.trim()).filter(Boolean);
  if (delen.length > 0) filters.sub_portfolio = [...new Set(delen)];
  return filters;
}

/** Van filter naar URL. Lege waarden staan niet in de URL. */
export function searchParamsFromFilters(filters: AssetFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of TEXT_KEYS) {
    const value = filters[key];
    if (value) params.set(key, value);
  }
  for (const key of CHOICE_KEYS) {
    // Eén `append` per waarde: dat is de vorm die de API leest, en de enige
    // waarin "E, F en G" drie waarden blijven in plaats van één tekst.
    for (const value of filters.choices?.[key] ?? []) params.append(key, value);
  }
  for (const key of RANGE_KEYS) {
    const bounds = filters.ranges?.[key];
    if (bounds?.from !== undefined) params.set(`${key}_from`, String(bounds.from));
    if (bounds?.to !== undefined) params.set(`${key}_to`, String(bounds.to));
  }
  if (filters.in_cashflow_only) params.set("in_cashflow_only", "true");
  for (const id of filters.sub_portfolio ?? []) params.append(SUB_PORTFOLIO_KEY, id);
  return params;
}

/**
 * Van `filters.query` uit de API (een bewaarde deelportefeuille) naar een URL.
 * Een lijst wordt één `append` per waarde, net als in `searchParamsFromFilters`.
 */
export function searchParamsFromQuery(
  query: Record<string, string | number | boolean | string[]>,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) value.forEach((item) => params.append(key, String(item)));
    else params.set(key, String(value));
  }
  return params;
}

/**
 * De filter als API-parameters. Axios serialiseert een array als herhaalde
 * sleutel zolang `paramsSerializer` daarop staat (zie `api/client.ts`), en dat
 * is precies wat de API verwacht.
 */
export function filtersToApiParams(
  filters: AssetFilters,
): Record<string, string | number | boolean | string[]> {
  const params: Record<string, string | number | boolean | string[]> = {};
  for (const key of TEXT_KEYS) {
    const value = filters[key];
    if (value) params[key] = value;
  }
  for (const key of CHOICE_KEYS) {
    const values = filters.choices?.[key];
    if (values && values.length > 0) params[key] = values;
  }
  for (const key of RANGE_KEYS) {
    const bounds = filters.ranges?.[key];
    if (bounds?.from !== undefined) params[`${key}_from`] = bounds.from;
    if (bounds?.to !== undefined) params[`${key}_to`] = bounds.to;
  }
  if (filters.in_cashflow_only) params.in_cashflow_only = true;
  if (filters.sub_portfolio && filters.sub_portfolio.length > 0) {
    params[SUB_PORTFOLIO_KEY] = filters.sub_portfolio;
  }
  return params;
}

export function hasActiveFilters(filters: AssetFilters): boolean {
  return searchParamsFromFilters(filters).toString().length > 0;
}

/** De waarden die voor één keuzekolom aanstaan. */
export function chosen(filters: AssetFilters, key: ChoiceKey): string[] {
  return filters.choices?.[key] ?? [];
}

/** Eén waarde aan- of uitzetten binnen een keuzekolom. */
export function toggleChoice(filters: AssetFilters, key: ChoiceKey, value: string): AssetFilters {
  const huidig = chosen(filters, key);
  const volgend = huidig.includes(value)
    ? huidig.filter((item) => item !== value)
    : [...huidig, value];
  return withChoice(filters, key, volgend);
}

/** Alle waarden van één keuzekolom vervangen. */
export function withChoice(filters: AssetFilters, key: ChoiceKey, values: string[]): AssetFilters {
  const choices = { ...(filters.choices ?? {}) };
  if (values.length === 0) delete choices[key];
  else choices[key] = values;
  const next: AssetFilters = { ...filters };
  // Leeg betekent "geen filter", en dan hoort de sleutel er niet te staan:
  // een leeg object zou `hasActiveFilters` niets schelen, maar het maakt wel
  // verschil voor wie de filter als geheel vergelijkt.
  if (Object.keys(choices).length === 0) delete next.choices;
  else next.choices = choices;
  return next;
}

/** Het van/tot-bereik van één kolom zetten of wissen. */
export function withRange(filters: AssetFilters, key: RangeKey, bounds: Bounds): AssetFilters {
  const ranges = { ...(filters.ranges ?? {}) };
  if (bounds.from === undefined && bounds.to === undefined) delete ranges[key];
  else ranges[key] = bounds;
  const next: AssetFilters = { ...filters };
  if (Object.keys(ranges).length === 0) delete next.ranges;
  else next.ranges = ranges;
  return next;
}

export function boundsOf(filters: AssetFilters, key: RangeKey): Bounds {
  return filters.ranges?.[key] ?? {};
}

/**
 * Eén filter wissen, op de sleutel waarmee de API hem beschrijft. Een bereik is
 * daar één item met één sleutel; die sleutel dekt beide grenzen.
 */
export function withoutFilter(filters: AssetFilters, key: string): AssetFilters {
  if ((CHOICE_KEYS as readonly string[]).includes(key)) {
    return withChoice(filters, key as ChoiceKey, []);
  }
  if ((RANGE_KEYS as readonly string[]).includes(key)) {
    return withRange(filters, key as RangeKey, {});
  }
  const next = { ...filters };
  delete next[key as TextKey | "in_cashflow_only" | typeof SUB_PORTFOLIO_KEY];
  return next;
}

/**
 * Staat er een filter op deze kolom? Op de sleutel waarmee de API hem
 * beschrijft, dezelfde als `withoutFilter` gebruikt.
 *
 * De kolomkiezer heeft dit nodig: een kolom die uit staat kan nog steeds
 * filteren, en dat hoort zichtbaar te blijven.
 */
export function isFilteredOn(filters: AssetFilters, key: string): boolean {
  if ((CHOICE_KEYS as readonly string[]).includes(key)) {
    return chosen(filters, key as ChoiceKey).length > 0;
  }
  if ((RANGE_KEYS as readonly string[]).includes(key)) {
    const bounds = boundsOf(filters, key as RangeKey);
    return bounds.from !== undefined || bounds.to !== undefined;
  }
  if (isTextKey(key)) return Boolean(filters[key]);
  if (key === "in_cashflow_only") return Boolean(filters.in_cashflow_only);
  if (key === SUB_PORTFOLIO_KEY) return (filters.sub_portfolio ?? []).length > 0;
  return false;
}

/** De gekozen deelportefeuilles vervangen; leeg haalt het filter weg. */
export function withSubPortfolios(filters: AssetFilters, ids: string[]): AssetFilters {
  const next: AssetFilters = { ...filters };
  if (ids.length === 0) delete next.sub_portfolio;
  else next.sub_portfolio = [...new Set(ids)];
  return next;
}

/**
 * Een filter zoals het register van de API hem beschrijft
 * (`asset_filters.register`). De samensteller op /portefeuille bouwt zich
 * hieruit op, zodat een filter erbij in de API vanzelf kiesbaar wordt
 * (Jan, FB-070: "op alle kolommen kunnen filteren").
 */
export type RegisterFilter = {
  key: string;
  label: string;
  kind: "choice" | "range" | "text" | "flag";
  /** Thema in de samensteller; zie `FilterRegister.groups`. */
  group: string;
  /**
   * Waar de waarde thuishoort. Een filter op pand of complex selecteert de
   * objecten daarin: een deelportefeuille blijft een verzameling objecten.
   */
  level: "object" | "pand" | "complex";
  unit?: string | null;
  integer?: boolean;
  /** Bij een tekstfilter: exact (een code) of een deel van de tekst. */
  exact?: boolean;
  /** Vaste keuzelijst; zonder deze komt de lijst uit `facets`. */
  options?: Array<{ value: string; label: string; count: number | null }>;
};

export type FilterRegister = {
  groups: Array<{ key: string; label: string }>;
  filters: RegisterFilter[];
};

export const isChoiceKey = (key: string): key is ChoiceKey =>
  (CHOICE_KEYS as readonly string[]).includes(key);

export const isRangeKey = (key: string): key is RangeKey =>
  (RANGE_KEYS as readonly string[]).includes(key);

export type TextKey = (typeof TEXT_KEYS)[number];

export const isTextKey = (key: string): key is TextKey =>
  (TEXT_KEYS as readonly string[]).includes(key);

/** Een tekstfilter zetten of wissen. */
export function withText(filters: AssetFilters, key: TextKey, value: string | undefined): AssetFilters {
  const next: AssetFilters = { ...filters };
  if (value) next[key] = value;
  else delete next[key];
  return next;
}

/** "Alleen eenheden in de kasstroom" aan- of uitzetten. */
export function withCashflowOnly(filters: AssetFilters, on: boolean): AssetFilters {
  const next: AssetFilters = { ...filters };
  if (on) next.in_cashflow_only = true;
  else delete next.in_cashflow_only;
  return next;
}

/** Een filter zoals de API hem in gewone taal beschrijft. */
export type ActiveFilter = { key: string; label: string; value: string };
